import ClearAllIcon from "@mui/icons-material/ClearAll";
import ReplayIcon from "@mui/icons-material/Replay";
import { Button, Divider, Stack, TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { APP_ICONS } from "../../assets";
import AppContainer from "../../components/containers/AppContainer";
import CustomBar from "../../components/customBar";
import CustomSnackbar from "../../components/customSnackBar/customSnackBar";
import DynamicTable from "../../components/dynamicTable";
import AppLoader from "../../components/loader/loader";
import NavigationDrawer from "../../components/navigationDrawer/index";
import TextInput from "../../components/textInput";
import { userStatusUpdate } from "../../services/customerFn";
import { getAllProvider, searchProviderData } from "../../services/providerFn";

const tableHeader = [
  "USER NAME",
  "PHONE",
  "EMAIL",
  "TOTAL HOURS",
  "TOTAL JOBS",
  "STATUS",
];

const displayRows = [
  "user_data",
  "phone",
  "email",
  "totalHours",
  "totalJobs",
  "profile_status",
];

const Providers = () => {
  const [searchValue, setSearchValue] = useState(
    localStorage.getItem("providerSearchValue") || ""
  );

  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");

  const [providerData, setProviderData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [loading, setLoading] = useState(true);
  const snackRef = useRef(null);

  const openSnack = (status, message) => {
    snackRef.current?.isOpenSnack({
      snackType: status,
      snackMessage: message,
    });
  };

  useEffect(() => {
    fetchProviderData();
  }, []);

  useEffect(() => {
    applyRuntimeFilter();
  }, [searchValue, providerData]);

  const fetchProviderData = async () => {
    try {
      setLoading(true);
      const response = await getAllProvider();

      if (response.data.status === "success") {
        setProviderData(response.data.data || []);
        setFilteredData(response.data.data || []);
      } else {
        openSnack("error", response.data?.message);
      }
    } catch (error) {
      console.error("Error fetching provider data:", error);
    } finally {
      setLoading(false);
    }
  };

  const applyRuntimeFilter = () => {
    if (!searchValue.trim()) {
      setFilteredData(providerData);
      return;
    }

    const query = searchValue.toLowerCase();

    const filtered = providerData.filter(
      (item) =>
        item.fullname?.toLowerCase().includes(query) ||
        item.email?.toLowerCase().includes(query) ||
        item.contact?.toLowerCase().includes(query)
    );

    setFilteredData(filtered);
  };

  const handleFilters = async () => {
    try {
      setLoading(true);

      const response = await searchProviderData(createdFrom, createdTo);

      if (response.data.status === "success") {
        const data = response.data.data || [];
        setProviderData(data);
        setFilteredData(data);
      } else {
        openSnack("error", response.data?.message);
      }
    } catch (error) {
      console.log(error);
      openSnack("error", "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    localStorage.setItem("providerSearchValue", value);
  };

  const handleResetFilters = () => {
    setSearchValue("");
    setCreatedFrom("");
    setCreatedTo("");

    localStorage.removeItem("providerSearchValue");

    fetchProviderData();
  };

  const handleChangeProviderStatus = async (value, row) => {
    try {
      setLoading(true);

      const response = await userStatusUpdate(row?._id, { status: value });

      if (response.data.status === "success") {
        fetchProviderData();
        openSnack("success", "Status updated successfully");
      } else {
        openSnack("error", response.data?.message);
      }
    } catch (error) {
      openSnack("error", "Error updating status");
    } finally {
      setLoading(false);
    }
  };

  return (
    <NavigationDrawer>
      <AppLoader isVisible={loading} />

      <CustomBar title={"WORKER REGISTER"} />

      <AppContainer>
        <Stack
          px={3}
          py={2}
          gap={2}
          direction={{ md: "row" }}
          alignItems={"center"}
        >
          <TextInput
            sx={{ flex: 1 }}
            placeholder={"Search Providers"}
            InputStartIcon={<img src={APP_ICONS.searhIcon} width={20} />}
            value={searchValue}
            onChange={handleSearchChange}
          />

          <TextField
            label="Created From"
            type="date"
            value={createdFrom}
            onChange={(e) => setCreatedFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 180 }}
            size="small"
          />

          <TextField
            label="Created To"
            type="date"
            value={createdTo}
            onChange={(e) => setCreatedTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 180 }}
            size="small"
          />

          <Button
            variant="outlined"
            startIcon={<ClearAllIcon />}
            onClick={handleFilters}
            sx={{ height: 50, width: 160 }}
          >
            Search
          </Button>

          <Button
            variant="contained"
            color="error"
            sx={{ height: 50, width: 140 }}
            startIcon={<ReplayIcon />}
            onClick={handleResetFilters}
          >
            Reset
          </Button>
        </Stack>

        <Divider />

        <DynamicTable
          tableHeader={tableHeader}
          displayRows={displayRows}
          tableData={filteredData}
          showPagination={true}
          handleChangeStatus={handleChangeProviderStatus}
          showUserMenu={true}
        />

        <CustomSnackbar ref={snackRef} />
      </AppContainer>
    </NavigationDrawer>
  );
};

export default Providers;
